import { useEffect } from 'react';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { useApp } from '@/lib/core/providers/appProvider';
import { setTalkdeskContext, setTalkdeskTouchpointId } from '@/lib/core/utils';
import { getConfig } from '../../../../config/environment';

/**
 * Keeps the Talkdesk webchat pointed at the right touchpoint and staged with the caller's
 * identity, so the concierge agent sees who they are talking to instead of "Visitor #NNN".
 *
 * Single choke point for both. This logic previously lived duplicated in `AppNavBar` and the
 * `Assistance` module — every entry point into the chat needs it, but only one of the two copies
 * received `authUser.phoneNumber` when it was added (commit 68ff9834), so the phone reached
 * Talkdesk from the navbar and not from the assistance screen. Any component that can open the
 * chat calls this hook; nothing else touches the module-level state in `utils/talkdesk.ts`.
 *
 * Callbacks (`setTalkdeskCallbacks`) are deliberately NOT handled here: the navbar uses them to
 * drive its active-concierge pill state while the assistance screen wants no-ops, so they are
 * genuinely per-call-site rather than duplication.
 */
/**
 * First value that is actually filled in.
 *
 * `??` is not enough here: it only falls through on `null`/`undefined`, so a field that comes back
 * as `''` — which the API does for a phone never captured at registration — would stop the chain
 * and hand Talkdesk an empty string while a perfectly good number sat further down it.
 */
function firstFilled(...values: Array<string | null | undefined>): string {
  return values.find(value => value?.trim())?.trim() ?? '';
}

export function useTalkdeskIdentitySync() {
  const { user, profile } = useApp();
  const authUser = useAuthStore(state => state.user);

  useEffect(() => {
    const { talkdeskTouchpointId, talkdeskTouchpointIdNonMember } = getConfig();
    const id = authUser?.hasActiveMembership ? talkdeskTouchpointId : talkdeskTouchpointIdNonMember;
    if (id) setTalkdeskTouchpointId(id);
  }, [authUser]);

  useEffect(() => {
    // `profile` must stay in the dependency list: appProvider resolves it strictly after `user`
    // (setUser at loadApp, then an awaited getUserProfile), so an effect keyed on `user` alone
    // runs once while `profile` is still undefined and never re-runs with the phone number.
    if (!authUser && !user) return;

    setTalkdeskContext({
      field_name1: firstFilled(authUser?.name, user?.name),
      field_email1: firstFilled(authUser?.email, user?.email),
      // Widest chain of the three, because a phone can legitimately be missing from every earlier
      // source. `user.phoneNumber` was absent while name and email both consulted it, and
      // `profile.otherPhone` — the only phone the member can actually edit, in Personal
      // information — was never read at all, so a member who filled it in still reached the
      // concierge as an unknown number.
      field_number1: firstFilled(
        authUser?.phoneNumber,
        user?.phoneNumber,
        profile?.phone,
        profile?.otherPhone
      ),
      salesforce_id: firstFilled(authUser?.salesforceId, user?.salesforceId, profile?.salesforceId),
    });
  }, [authUser, user, profile]);
}
